import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, MouseSensor, TouchSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ListChecks, Plus, ArrowLeft, MoreVertical, Pencil, Trash2, Copy, Share2, Lock,
  Users, ChevronUp, ChevronDown, X, Check, Loader2, GripVertical,
} from 'lucide-react';

// One draggable list-item row. Drag is bound ONLY to the ☰ handle so taps on the
// checkbox / edit / delete / move buttons keep working normally.
const SortableListItem = ({ item, idx, count, canEdit, t, onToggle, onMove, onEdit, onDelete, Checkbox, Button }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: !canEdit });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 20 : 'auto' };
  return (
    <div ref={setNodeRef} style={style} className="bg-white rounded-lg border p-2.5 flex items-center gap-2" data-testid={`lists-item-${item.id}`}>
      {canEdit && (
        <button type="button" className="shrink-0 flex items-center justify-center h-8 w-7 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 cursor-grab active:cursor-grabbing touch-none"
          aria-label={t('lists.dragHandle', 'Drag to reorder')} title={t('lists.dragHandle', 'Drag to reorder')} data-testid={`lists-item-drag-${item.id}`}
          {...attributes} {...listeners}>
          <GripVertical className="h-5 w-5" />
        </button>
      )}
      <Checkbox checked={!!item.completed} disabled={!canEdit}
        onCheckedChange={() => canEdit && onToggle(item)} data-testid={`lists-item-check-${item.id}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${item.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>{item.text}
          {item.quantity ? <span className="text-gray-400 ml-2">× {item.quantity}</span> : null}
        </p>
        {item.note ? <p className="text-[11px] text-gray-400 truncate">{item.note}</p> : null}
      </div>
      {canEdit && (
        <div className="flex items-center gap-0.5 shrink-0">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={idx === 0} onClick={() => onMove(idx, -1)} data-testid={`lists-item-up-${item.id}`}><ChevronUp className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={idx === count - 1} onClick={() => onMove(idx, 1)} data-testid={`lists-item-down-${item.id}`}><ChevronDown className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-600" onClick={() => onEdit(item)} data-testid={`lists-item-edit-${item.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => onDelete(item)} data-testid={`lists-item-delete-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
};

// Draggable wrapper for a whole LIST card on the overview. Drag is bound to the
// ☰ handle only, so tapping the card still opens the list.
const SortableListCard = ({ id, t, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 20 : 'auto' };
  return (
    <div ref={setNodeRef} style={style} className="flex items-stretch gap-1">
      <button type="button" className="shrink-0 flex items-center justify-center w-7 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 cursor-grab active:cursor-grabbing touch-none"
        aria-label={t('lists.dragHandle', 'Drag to reorder')} title={t('lists.dragHandle', 'Drag to reorder')}
        data-testid={`lists-card-drag-${id}`} {...attributes} {...listeners}>
        <GripVertical className="h-5 w-5" />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};

const LIST_PALETTE = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e', '#10b981',
  '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
  '#d946ef', '#ec4899', '#f43f5e', '#64748b',
];

const ListsPanel = ({ calendarId, role }) => {
  const { t } = useTranslation();
  const writable = ['owner', 'admin', 'member'].includes(role);

  const [loading, setLoading] = useState(true);
  const [lists, setLists] = useState([]);
  const [openListId, setOpenListId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // create/edit list dialog
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [listForm, setListForm] = useState({ name: '', color: LIST_PALETTE[9], visibility: 'private' });
  const [savingList, setSavingList] = useState(false);

  // share dialog
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmails, setShareEmails] = useState([]);
  const [shareInput, setShareInput] = useState('');
  const [savingShare, setSavingShare] = useState(false);

  // delete confirm
  const [confirmDeleteList, setConfirmDeleteList] = useState(null);

  // item input
  const [newItemText, setNewItemText] = useState('');
  const [newItemQty, setNewItemQty] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({ text: '', quantity: '' });
  const [savingItem, setSavingItem] = useState(false);

  const loadLists = useCallback(async () => {
    if (!calendarId) return;
    setLoading(true);
    try {
      const r = await axiosInstance.get(`/calendar/calendars/${calendarId}/lists`);
      setLists(r.data.lists || []);
    } catch (e) {
      toast.error(t('lists.loadError', 'Could not load lists'));
    } finally {
      setLoading(false);
    }
  }, [calendarId, t]);

  useEffect(() => { loadLists(); }, [loadLists]);

  const openList = async (id) => {
    setOpenListId(id);
    setDetailLoading(true);
    try {
      const r = await axiosInstance.get(`/calendar/lists/${id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(t('lists.openError', 'This list is not available'));
      setOpenListId(null);
      loadLists();
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!openListId) return;
    const r = await axiosInstance.get(`/calendar/lists/${openListId}`);
    setDetail(r.data);
  };

  const closeList = () => { setOpenListId(null); setDetail(null); loadLists(); };

  // ---- list CRUD ----
  const openCreate = () => {
    setEditingList(null);
    setListForm({ name: '', color: LIST_PALETTE[9], visibility: 'private' });
    setListDialogOpen(true);
  };
  const openEditList = (lst) => {
    setEditingList(lst);
    setListForm({ name: lst.name, color: lst.color, visibility: lst.visibility });
    setListDialogOpen(true);
  };
  const saveList = async () => {
    if (!listForm.name.trim()) return;
    setSavingList(true);
    try {
      if (editingList) {
        await axiosInstance.patch(`/calendar/lists/${editingList.id}`, {
          name: listForm.name.trim(), color: listForm.color, visibility: listForm.visibility,
        });
        toast.success(t('lists.updated', 'List updated'));
        if (openListId === editingList.id) await refreshDetail();
      } else {
        await axiosInstance.post(`/calendar/calendars/${calendarId}/lists`, {
          name: listForm.name.trim(), color: listForm.color, visibility: listForm.visibility,
        });
        toast.success(t('lists.created', 'List created'));
      }
      setListDialogOpen(false);
      await loadLists();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('lists.saveError', 'Could not save list'));
    } finally {
      setSavingList(false);
    }
  };
  const deleteList = async () => {
    const lst = confirmDeleteList;
    try {
      await axiosInstance.delete(`/calendar/lists/${lst.id}`);
      toast.success(t('lists.deleted', 'List deleted'));
      if (openListId === lst.id) closeList(); else loadLists();
    } catch (e) {
      toast.error(t('lists.deleteError', 'Could not delete list'));
    } finally {
      setConfirmDeleteList(null);
    }
  };
  const duplicateList = async (lst) => {
    try {
      await axiosInstance.post(`/calendar/lists/${lst.id}/duplicate`);
      toast.success(t('lists.duplicated', 'A private copy was created'));
      await loadLists();
    } catch (e) {
      toast.error(t('lists.dupError', 'Could not duplicate list'));
    }
  };

  // ---- sharing ----
  const openShare = (lst) => {
    setEditingList(lst);
    setShareEmails((lst.sharedWithDetails || []).map(d => d.email).filter(Boolean));
    setShareInput('');
    setShareOpen(true);
  };
  const addShareEmail = () => {
    const e = shareInput.trim();
    if (e && !shareEmails.some(x => x.toLowerCase() === e.toLowerCase())) {
      setShareEmails([...shareEmails, e]);
    }
    setShareInput('');
  };
  const saveShare = async () => {
    setSavingShare(true);
    try {
      await axiosInstance.patch(`/calendar/lists/${editingList.id}/share`, { emails: shareEmails });
      toast.success(t('lists.shareSaved', 'Sharing updated'));
      setShareOpen(false);
      await loadLists();
      if (openListId === editingList.id) await refreshDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('lists.shareError', 'Could not update sharing'));
    } finally {
      setSavingShare(false);
    }
  };

  // ---- items ----
  const addItem = async () => {
    if (!newItemText.trim() || !detail || addingItem) return;
    const text = newItemText.trim();
    const qty = newItemQty.trim();
    setNewItemText(''); setNewItemQty('');  // clear up-front so fast typing isn't wiped by a late resolve
    setAddingItem(true);
    try {
      await axiosInstance.post(`/calendar/lists/${detail.id}/items`, { text, quantity: qty });
      await refreshDetail();
    } catch (e) {
      setNewItemText(text); setNewItemQty(qty);  // restore on failure
      toast.error(e?.response?.data?.detail || t('lists.itemError', 'Could not add item'));
    } finally {
      setAddingItem(false);
    }
  };
  const toggleItem = async (item) => {
    // optimistic
    setDetail(d => ({ ...d, items: d.items.map(i => i.id === item.id ? { ...i, completed: !i.completed } : i) }));
    try {
      await axiosInstance.patch(`/calendar/lists/${detail.id}/items/${item.id}`, { completed: !item.completed });
    } catch (e) {
      toast.error(t('lists.itemError2', 'Could not update item'));
      await refreshDetail();
    }
  };
  const editItemText = async (item, text) => {
    if (text.trim() === item.text) return;
    try {
      await axiosInstance.patch(`/calendar/lists/${detail.id}/items/${item.id}`, { text: text.trim() });
      await refreshDetail();
    } catch (e) { toast.error(t('lists.itemError2', 'Could not update item')); }
  };
  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({ text: item.text || '', quantity: item.quantity || '' });
  };
  const saveItem = async () => {
    if (!editingItem) return;
    const text = itemForm.text.trim();
    const quantity = itemForm.quantity.trim();
    if (!text) { toast.error(t('lists.itemTextRequired', 'Item text is required')); return; }
    setSavingItem(true);
    try {
      // Reuse the existing item-update endpoint — updates the SAME item in place.
      await axiosInstance.patch(`/calendar/lists/${detail.id}/items/${editingItem.id}`, { text, quantity });
      setDetail(d => ({ ...d, items: d.items.map(i => i.id === editingItem.id ? { ...i, text, quantity } : i) }));
      setEditingItem(null);
      toast.success(t('lists.itemUpdated', 'Item updated'));
    } catch (e) {
      toast.error(t('lists.itemError2', 'Could not update item'));
      await refreshDetail();
    } finally { setSavingItem(false); }
  };
  const deleteItem = async (item) => {
    setDetail(d => ({ ...d, items: d.items.filter(i => i.id !== item.id) }));
    try {
      await axiosInstance.delete(`/calendar/lists/${detail.id}/items/${item.id}`);
    } catch (e) { toast.error(t('lists.itemDelError', 'Could not delete item')); await refreshDetail(); }
  };
  const persistOrder = async (items) => {
    setDetail(d => ({ ...d, items }));
    try {
      await axiosInstance.patch(`/calendar/lists/${detail.id}/reorder`, { itemIds: items.map(i => i.id) });
    } catch (e) { toast.error(t('lists.reorderError', 'Could not reorder')); await refreshDetail(); }
  };
  // Reorder the LISTS themselves on the overview (persists server-side).
  const persistListOrder = async (newLists) => {
    setLists(newLists);
    try {
      await axiosInstance.patch(`/calendar/calendars/${calendarId}/lists/reorder`, { listIds: newLists.map(l => l.id) });
    } catch (e) { toast.error(t('lists.reorderError', 'Could not reorder')); await loadLists(); }
  };
  const handleListDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = lists.findIndex(l => l.id === active.id);
    const to = lists.findIndex(l => l.id === over.id);
    if (from === -1 || to === -1) return;
    await persistListOrder(arrayMove(lists, from, to));
  };
  const moveItem = async (index, dir) => {
    const items = [...detail.items];
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    [items[index], items[j]] = [items[j], items[index]];
    await persistOrder(items);
  };
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const items = detail.items;
    const from = items.findIndex(i => i.id === active.id);
    const to = items.findIndex(i => i.id === over.id);
    if (from === -1 || to === -1) return;
    await persistOrder(arrayMove(items, from, to));
  };
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ================= RENDER: LIST DETAIL =================
  if (openListId) {
    const canEdit = detail?.canEdit;
    const canManage = detail?.canManage;
    const items = detail?.items || [];
    return (
      <div className="space-y-3" data-testid="lists-detail">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={closeList} data-testid="lists-back">
            <ArrowLeft className="h-4 w-4 mr-1" />{t('lists.back', 'Lists')}
          </Button>
        </div>
        {detailLoading || !detail ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-purple-600" /></div>
        ) : (
          <>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: detail.color }} />
                  <h2 className="text-base font-bold text-gray-900 truncate" data-testid="lists-detail-title">{detail.name}</h2>
                  {detail.visibility === 'private'
                    ? <Badge variant="secondary" className="text-[10px] gap-1"><Lock className="h-3 w-3" />{t('lists.private', 'Private')}</Badge>
                    : <Badge variant="secondary" className="text-[10px] gap-1"><Users className="h-3 w-3" />{t('lists.shared', 'Shared')}</Badge>}
                </div>
                {canManage && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid="lists-detail-menu"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditList(detail)} data-testid="lists-detail-edit"><Pencil className="h-4 w-4 mr-2" />{t('lists.editList', 'Edit list')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openShare(detail)} data-testid="lists-detail-share"><Share2 className="h-4 w-4 mr-2" />{t('lists.manageSharing', 'Manage sharing')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => duplicateList(detail)} data-testid="lists-detail-duplicate"><Copy className="h-4 w-4 mr-2" />{t('lists.duplicate', 'Duplicate')}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600" onClick={() => setConfirmDeleteList(detail)} data-testid="lists-detail-delete"><Trash2 className="h-4 w-4 mr-2" />{t('lists.deleteList', 'Delete list')}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {!canManage && (
                  <Button size="sm" variant="ghost" onClick={() => duplicateList(detail)} data-testid="lists-detail-duplicate"><Copy className="h-4 w-4 mr-1" />{t('lists.duplicate', 'Duplicate')}</Button>
                )}
              </div>
              {detail.visibility === 'shared' && (detail.sharedWithDetails || []).length > 0 && (
                <p className="text-[11px] text-gray-500 mt-2">{t('lists.sharedWith', 'Shared with')}: {(detail.sharedWithDetails || []).map(d => d.name || d.email).join(', ')}</p>
              )}
              {!canEdit && (
                <p className="text-[11px] text-amber-600 mt-2" data-testid="lists-readonly-note">{t('lists.readOnly', 'You have read-only access to this list.')}</p>
              )}
            </div>

            {canEdit && (
              <div className="bg-white rounded-xl border p-3 flex items-end gap-2" data-testid="lists-add-item-row">
                <div className="flex-1">
                  <Label className="text-[11px]">{t('lists.newItem', 'New item')}</Label>
                  <Input value={newItemText} onChange={e => setNewItemText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addItem(); }}
                    placeholder={t('lists.itemPlaceholder', 'e.g. Milk')} data-testid="lists-item-input" />
                </div>
                <div className="w-24">
                  <Label className="text-[11px]">{t('lists.qty', 'Qty')}</Label>
                  <Input value={newItemQty} onChange={e => setNewItemQty(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addItem(); }}
                    placeholder="1" data-testid="lists-item-qty" />
                </div>
                <Button onClick={addItem} disabled={addingItem || !newItemText.trim()} data-testid="lists-item-add">
                  {addingItem ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
            )}

            {canEdit && items.length > 1 && (
              <div className="flex items-center gap-1.5 text-[11px] text-gray-500 px-1" data-testid="lists-reorder-hint">
                <GripVertical className="h-3.5 w-3.5 text-gray-400" />
                {t('lists.reorderHint', 'Drag the handle to reorder, or use the ▲ ▼ arrows')}
              </div>
            )}
            <div className="space-y-1.5" data-testid="lists-items">
              {items.length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-10" data-testid="lists-items-empty">{t('lists.noItems', 'No items yet.')}</div>
              ) : canEdit ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1.5">
                      {items.map((item, idx) => (
                        <SortableListItem key={item.id} item={item} idx={idx} count={items.length} canEdit={canEdit} t={t}
                          onToggle={toggleItem} onMove={moveItem} onEdit={openEditItem} onDelete={deleteItem}
                          Checkbox={Checkbox} Button={Button} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                items.map((item) => (
                  <div key={item.id} className="bg-white rounded-lg border p-2.5 flex items-center gap-2" data-testid={`lists-item-${item.id}`}>
                    <Checkbox checked={!!item.completed} disabled data-testid={`lists-item-check-${item.id}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${item.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>{item.text}
                        {item.quantity ? <span className="text-gray-400 ml-2">× {item.quantity}</span> : null}
                      </p>
                      {item.note ? <p className="text-[11px] text-gray-400 truncate">{item.note}</p> : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
        {renderDialogs()}
      </div>
    );
  }

  // ================= RENDER: LIST OVERVIEW =================
  function renderDialogs() {
    return (
      <>
        <Dialog open={!!editingItem} onOpenChange={o => { if (!o) setEditingItem(null); }}>
          <DialogContent className="max-w-sm" data-testid="lists-item-edit-dialog">
            <DialogHeader>
              <DialogTitle>{t('lists.editItem', 'Edit item')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-[12px] text-gray-500">{t('lists.itemText', 'Item')}</label>
                <Input value={itemForm.text} maxLength={300} autoFocus
                  onChange={e => setItemForm(f => ({ ...f, text: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') saveItem(); }}
                  data-testid="lists-item-edit-text" />
              </div>
              <div>
                <label className="text-[12px] text-gray-500">{t('lists.quantity', 'Quantity')}</label>
                <Input value={itemForm.quantity} maxLength={40}
                  onChange={e => setItemForm(f => ({ ...f, quantity: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') saveItem(); }}
                  placeholder="1" data-testid="lists-item-edit-qty" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingItem(null)} disabled={savingItem} data-testid="lists-item-edit-cancel">{t('lists.cancel', 'Cancel')}</Button>
              <Button onClick={saveItem} disabled={savingItem || !itemForm.text.trim()} data-testid="lists-item-edit-save">{t('lists.save', 'Save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={listDialogOpen} onOpenChange={setListDialogOpen}>
          <DialogContent className="max-w-sm" data-testid="lists-form-dialog">
            <DialogHeader>
              <DialogTitle>{editingList ? t('lists.editList', 'Edit list') : t('lists.createList', 'Create list')}</DialogTitle>
              <DialogDescription>{t('lists.formHint', 'Name your list, pick a colour and choose who can see it.')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[11px]">{t('lists.name', 'Name')}</Label>
                <Input value={listForm.name} onChange={e => setListForm(f => ({ ...f, name: e.target.value }))} data-testid="lists-form-name" />
              </div>
              <div>
                <Label className="text-[11px]">{t('lists.colour', 'Colour')}</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {LIST_PALETTE.map(c => (
                    <button key={c} type="button" onClick={() => setListForm(f => ({ ...f, color: c }))}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${listForm.color === c ? 'border-gray-900' : 'border-transparent'}`}
                      style={{ backgroundColor: c }} data-testid={`lists-form-color-${c}`}>
                      {listForm.color === c && <Check className="h-3 w-3 text-white" />}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-[11px]">{t('lists.privacy', 'Privacy')}</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button type="button" onClick={() => setListForm(f => ({ ...f, visibility: 'private' }))}
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm ${listForm.visibility === 'private' ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600'}`}
                    data-testid="lists-form-private"><Lock className="h-4 w-4" />{t('lists.private', 'Private')}</button>
                  <button type="button" onClick={() => setListForm(f => ({ ...f, visibility: 'shared' }))}
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm ${listForm.visibility === 'shared' ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600'}`}
                    data-testid="lists-form-shared"><Users className="h-4 w-4" />{t('lists.shared', 'Shared')}</button>
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  {listForm.visibility === 'private'
                    ? t('lists.privateHint', 'Only you can see and manage this list.')
                    : t('lists.sharedHint', 'You choose exactly which calendar members can access it.')}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setListDialogOpen(false)} data-testid="lists-form-cancel">{t('lists.cancel', 'Cancel')}</Button>
              <Button onClick={saveList} disabled={savingList || !listForm.name.trim()} data-testid="lists-form-save">
                {savingList ? <Loader2 className="h-4 w-4 animate-spin" /> : t('lists.save', 'Save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogContent className="max-w-sm" data-testid="lists-share-dialog">
            <DialogHeader>
              <DialogTitle>{t('lists.manageSharing', 'Manage sharing')}</DialogTitle>
              <DialogDescription>{t('lists.shareHint', 'Add the exact email of a calendar member. They must already belong to this calendar.')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-[11px]">{t('lists.memberEmail', 'Member email')}</Label>
                  <Input value={shareInput} onChange={e => setShareInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addShareEmail(); } }}
                    placeholder="name@example.com" data-testid="lists-share-input" />
                </div>
                <Button variant="outline" onClick={addShareEmail} data-testid="lists-share-add"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {shareEmails.length === 0 && <p className="text-[11px] text-gray-400">{t('lists.noneShared', 'Not shared with anyone yet.')}</p>}
                {shareEmails.map(e => (
                  <Badge key={e} variant="secondary" className="gap-1" data-testid={`lists-share-chip-${e}`}>
                    {e}
                    <button onClick={() => setShareEmails(shareEmails.filter(x => x !== e))}><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShareOpen(false)} data-testid="lists-share-cancel">{t('lists.cancel', 'Cancel')}</Button>
              <Button onClick={saveShare} disabled={savingShare} data-testid="lists-share-save">
                {savingShare ? <Loader2 className="h-4 w-4 animate-spin" /> : t('lists.save', 'Save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!confirmDeleteList} onOpenChange={o => !o && setConfirmDeleteList(null)}>
          <AlertDialogContent data-testid="lists-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('lists.deleteList', 'Delete list')}?</AlertDialogTitle>
              <AlertDialogDescription>{t('lists.deleteConfirm', 'This permanently deletes the list and all its items. This cannot be undone.')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="lists-delete-cancel">{t('lists.cancel', 'Cancel')}</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={deleteList} data-testid="lists-delete-confirm-btn">{t('lists.delete', 'Delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return (
    <div className="space-y-3" data-testid="lists-overview">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-gray-500">{t('lists.overviewHint', 'Private and shared checklists for this calendar.')}</p>
        <Button size="sm" onClick={openCreate} data-testid="lists-create-open"><Plus className="h-4 w-4 mr-1" />{t('lists.createList', 'Create list')}</Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-purple-600" /></div>
      ) : lists.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border" data-testid="lists-empty">
          <ListChecks className="h-10 w-10 mx-auto text-gray-300" />
          <p className="text-sm text-gray-500 mt-2">{t('lists.emptyTitle', 'No lists yet')}</p>
          <p className="text-[12px] text-gray-400">{t('lists.emptyHint', 'Create your first private or shared list.')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {lists.length > 1 && (
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 px-1" data-testid="lists-overview-reorder-hint">
              <GripVertical className="h-3.5 w-3.5 text-gray-400" />
              {t('lists.reorderListsHint', 'Drag a list by its handle to reorder')}
            </div>
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleListDragEnd}>
            <SortableContext items={lists.map(l => l.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {lists.map(lst => (
                  <SortableListCard key={lst.id} id={lst.id} t={t}>
                    <div className="bg-white rounded-xl border p-3 flex items-center gap-3 cursor-pointer hover:border-purple-300 transition-colors"
                      onClick={() => openList(lst.id)} data-testid={`lists-card-${lst.id}`}>
                      <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{ backgroundColor: lst.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900 truncate">{lst.name}</p>
                          {lst.visibility === 'private'
                            ? <Lock className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                            : <Users className="h-3.5 w-3.5 text-gray-400 shrink-0" />}
                          {!lst.isOwner && <Badge variant="secondary" className="text-[9px]">{t('lists.sharedTag', 'shared with you')}</Badge>}
                        </div>
                        <p className="text-[11px] text-gray-400">{lst.completedCount}/{lst.itemCount} {t('lists.done', 'done')}</p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`lists-card-menu-${lst.id}`}><MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
                          <DropdownMenuItem onClick={() => duplicateList(lst)} data-testid={`lists-card-duplicate-${lst.id}`}><Copy className="h-4 w-4 mr-2" />{t('lists.duplicate', 'Duplicate')}</DropdownMenuItem>
                          {lst.canManage && <>
                            <DropdownMenuItem onClick={() => openEditList(lst)} data-testid={`lists-card-edit-${lst.id}`}><Pencil className="h-4 w-4 mr-2" />{t('lists.editList', 'Edit list')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openShare(lst)} data-testid={`lists-card-share-${lst.id}`}><Share2 className="h-4 w-4 mr-2" />{t('lists.manageSharing', 'Manage sharing')}</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-600" onClick={() => setConfirmDeleteList(lst)} data-testid={`lists-card-delete-${lst.id}`}><Trash2 className="h-4 w-4 mr-2" />{t('lists.deleteList', 'Delete list')}</DropdownMenuItem>
                          </>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </SortableListCard>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
      {renderDialogs()}
    </div>
  );
};

export default ListsPanel;
